// src/pages/EmployeeLeavePolicy.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Container, Row, Col, Card, CardBody, CardHeader, Button, Badge,
    Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label,
    Input, Alert, Spinner, Table,
} from 'reactstrap';
import {
    RefreshCw, UserPlus, Eye, Trash2, Users, Search, XCircle,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from '../../Components/Common/TableContainer';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';

// ---------- Types ----------
interface LeavePolicy {
    id: number;
    name: string;
    is_active: boolean;
}

interface AssignmentRow {
    id: number;
    name: string;
    employee_code: string;
    designation?: { id: number; title: string };
    policy: {
        id: number;
        name: string;
        is_active: boolean;
    } | null;
    assignment: {
        id: number;
        effective_from: string;
        effective_to: string | null;
    } | null;
}

interface Pagination {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

const EmployeeLeavePolicy: React.FC = () => {
    // ---------- State ----------
    const [rows, setRows] = useState<AssignmentRow[]>([]);
    const [policies, setPolicies] = useState<LeavePolicy[]>([]);
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        limit: 10,
        totalCount: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Filters
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [policyFilter, setPolicyFilter] = useState<number | ''>('');

    // Assign modal
    const [assignModal, setAssignModal] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [assignData, setAssignData] = useState<{
        employee: AssignmentRow | null;
        policy_id: number | '';
    }>({ employee: null, policy_id: '' });
    const [assignError, setAssignError] = useState('');

    // History modal
    const [historyModal, setHistoryModal] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyData, setHistoryData] = useState<any[]>([]);
    const [historyEmployee, setHistoryEmployee] = useState<AssignmentRow | null>(null);

    // Unassign modal
    const [unassignModal, setUnassignModal] = useState(false);
    const [unassigning, setUnassigning] = useState(false);
    const [unassignTarget, setUnassignTarget] = useState<AssignmentRow | null>(null);

    // ---------- Fetchers ----------
    const fetchAssignments = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params: any = {
                page: pagination.page,
                limit: pagination.limit,
            };
            if (search) params.search = search;
            if (policyFilter) params.policy_id = policyFilter;

            const res: any = await apiClient.get('/leave-policies/assignments', params);
            setRows(res.data || []);
            if (res.pagination) setPagination(res.pagination);
        } catch (err: any) {
            setError(err.message || 'Failed to load assignments');
            toast.error('Failed to load assignments');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, search, policyFilter]);

    const fetchPolicies = useCallback(async () => {
        try {
            const res = (await apiClient.get('/leave-policies')) as LeavePolicy[];
            setPolicies(res || []);
        } catch (err: any) {
            console.error('Failed to load policies', err);
        }
    }, []);

    useEffect(() => {
        fetchPolicies();
    }, [fetchPolicies]);

    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);

    // ---------- Helpers ----------
    const formatDate = (d: string | null | undefined) => {
        if (!d) return '—';
        try {
            return format(new Date(d), 'dd MMM yyyy');
        } catch {
            return '—';
        }
    };

    // ---------- Search ----------
    const applySearch = () => {
        setSearch(searchInput.trim());
        setPagination(p => ({ ...p, page: 1 }));
    };

    const clearFilters = () => {
        setSearch('');
        setSearchInput('');
        setPolicyFilter('');
        setPagination(p => ({ ...p, page: 1 }));
    };

    // ---------- Assign ----------
    const openAssignModal = (row: AssignmentRow) => {
        setAssignData({ employee: row, policy_id: '' });
        setAssignError('');
        setAssignModal(true);
    };

    const closeAssignModal = () => {
        setAssignModal(false);
        setAssignData({ employee: null, policy_id: '' });
        setAssignError('');
    };

    const handleAssign = async () => {
        if (!assignData.employee || !assignData.policy_id) {
            setAssignError('Select a policy');
            return;
        }
        setAssigning(true);
        setAssignError('');
        try {
            await apiClient.post('/leave-policies/assign', {
                employee_id: assignData.employee.id,
                policy_id: Number(assignData.policy_id),
            });
            toast.success('Policy assigned');
            closeAssignModal();
            fetchAssignments();
        } catch (err: any) {
            setAssignError(err.message || 'Failed to assign');
            toast.error('Failed to assign policy');
        } finally {
            setAssigning(false);
        }
    };

    // ---------- History ----------
    const openHistoryModal = async (row: AssignmentRow) => {
        setHistoryEmployee(row);
        setHistoryModal(true);
        setHistoryLoading(true);
        try {
            const res: any = await apiClient.get(
                `/leave-policies/assignments/${row.id}/history`
            );
            setHistoryData(res || []);
        } catch (err: any) {
            toast.error('Failed to load history');
            setHistoryData([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    // ---------- Unassign ----------
    const openUnassignModal = (row: AssignmentRow) => {
        setUnassignTarget(row);
        setUnassignModal(true);
    };

    const closeUnassignModal = () => {
        setUnassignModal(false);
        setUnassignTarget(null);
    };

    const handleUnassign = async () => {
        if (!unassignTarget) return;
        setUnassigning(true);
        try {
            await apiClient.delete(
                `/leave-policies/assignments/${unassignTarget.id}`
            );
            toast.success('Policy unassigned');
            closeUnassignModal();
            fetchAssignments();
        } catch (err: any) {
            toast.error(err.message || 'Failed to unassign');
        } finally {
            setUnassigning(false);
        }
    };

    // ---------- Table Columns ----------
    const columns = useMemo(
        () => [
            {
                header: 'SI.No',
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (c: any) =>
                    (pagination.page - 1) * pagination.limit + c.row.index + 1,
            },
            {
                header: 'Employee',
                accessorKey: 'name',
                cell: (c: any) => {
                    const row: AssignmentRow = c.row.original;
                    return (
                        <div className="d-flex flex-column">
                            <span className="fw-semibold">{row.name}</span>
                            <small className="text-muted">{row.employee_code}</small>
                        </div>
                    );
                },
            },
            {
                header: 'Designation',
                accessorKey: 'designation',
                enableColumnFilter: false,
                cell: (c: any) => c.row.original.designation?.title || '—',
            },
            {
                header: 'Current Policy',
                accessorKey: 'policy',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (c: any) => {
                    const row: AssignmentRow = c.row.original;
                    if (!row.policy) {
                        return <Badge color="secondary">Not Assigned</Badge>;
                    }
                    return (
                        <div>
                            <Badge color={row.policy.is_active ? 'success' : 'warning'}>
                                {row.policy.name}
                            </Badge>
                            {row.assignment && (
                                <div>
                                    <small className="text-muted">
                                        Since {formatDate(row.assignment.effective_from)}
                                    </small>
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                header: 'Actions',
                accessorKey: 'actions',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (c: any) => {
                    const row: AssignmentRow = c.row.original;
                    return (
                        <div className="d-flex gap-1">
                            <Button
                                color="info"
                                size="sm"
                                title="History"
                                onClick={() => openHistoryModal(row)}
                            >
                                <Eye size={14} />
                            </Button>
                            <Button
                                color="primary"
                                size="sm"
                                title={row.policy ? 'Change Policy' : 'Assign Policy'}
                                onClick={() => openAssignModal(row)}
                            >
                                <UserPlus size={14} />
                            </Button>
                            {row.policy && (
                                <Button
                                    color="danger"
                                    size="sm"
                                    title="Unassign"
                                    onClick={() => openUnassignModal(row)}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ],
        [pagination.page, pagination.limit]
    );

    // ---------- Render ----------
    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb
                    title="Employee Leave Policy"
                    pageTitle="Employee Leave Policy"
                />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <h5 className="card-title mb-0">
                                    <Users size={18} className="me-2" />
                                    Employee Leave Policy Assignments
                                </h5>
                                <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={fetchAssignments}
                                    disabled={loading}
                                >
                                    <RefreshCw size={14} className="me-1" /> Refresh
                                </Button>
                            </CardHeader>

                            <CardBody>
                                {/* Filters */}
                                <Row className="g-2 mb-3 align-items-end">
                                    <Col md={4}>
                                        <Label className="form-label-sm">
                                            Search Employee
                                        </Label>
                                        <div className="d-flex gap-2">
                                            <Input
                                                bsSize="sm"
                                                value={searchInput}
                                                onChange={e => setSearchInput(e.target.value)}
                                                onKeyDown={e =>
                                                    e.key === 'Enter' && applySearch()
                                                }
                                                placeholder="Name or employee code"
                                            />
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={applySearch}
                                            >
                                                <Search size={14} />
                                            </Button>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <Label className="form-label-sm">
                                            Filter by Policy
                                        </Label>
                                        <Input
                                            type="select"
                                            bsSize="sm"
                                            value={policyFilter}
                                            onChange={e => {
                                                setPolicyFilter(
                                                    e.target.value
                                                        ? Number(e.target.value)
                                                        : ''
                                                );
                                                setPagination(p => ({ ...p, page: 1 }));
                                            }}
                                        >
                                            <option value="">All Policies</option>
                                            {policies.map(p => (
                                                <option key={p.id} value={p.id}>
                                                    {p.name}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                    <Col md={4}>
                                        <Button
                                            color="light"
                                            size="sm"
                                            onClick={clearFilters}
                                        >
                                            <XCircle size={14} className="me-1" /> Clear
                                        </Button>
                                    </Col>
                                </Row>

                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        {error}
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" />
                                        <span>Loading assignments...</span>
                                    </div>
                                ) : rows.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Users size={48} className="text-muted mb-3" />
                                        <h5>No employees found</h5>
                                        <p className="text-muted">
                                            Try adjusting your search or filters.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <TableContainer
                                            columns={columns}
                                            data={rows}
                                            customPageSize={pagination.limit}
                                            tableClass="table-striped"
                                            theadClass="table-light"
                                            manualPagination={true}
                                            pageCount={pagination.totalPages}         // total pages from server
                                            currentPage={pagination.page - 1}         // tanstack is 0-indexed
                                            onPageChange={(page) =>                    // page is 0-indexed
                                                setPagination(p => ({ ...p, page: page + 1 }))
                                            }
                                            onPageSizeChange={(size) =>
                                                setPagination(p => ({ ...p, page: 1, limit: size }))
                                            }
                                            totalRecords={pagination.totalCount}
                                        />

                                        {/* Manual pagination (TableContainer already has its own — remove if redundant) */}
                                        {/* {pagination.totalPages > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <small className="text-muted">
                                                    Showing{' '}
                                                    {(pagination.page - 1) * pagination.limit + 1}–
                                                    {Math.min(
                                                        pagination.page * pagination.limit,
                                                        pagination.totalCount
                                                    )}{' '}
                                                    of {pagination.totalCount}
                                                </small>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        color="light"
                                                        disabled={!pagination.hasPreviousPage}
                                                        onClick={() =>
                                                            setPagination(p => ({
                                                                ...p,
                                                                page: p.page - 1,
                                                            }))
                                                        }
                                                    >
                                                        Prev
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        color="light"
                                                        disabled={!pagination.hasNextPage}
                                                        onClick={() =>
                                                            setPagination(p => ({
                                                                ...p,
                                                                page: p.page + 1,
                                                            }))
                                                        }
                                                    >
                                                        Next
                                                    </Button>
                                                </div>
                                            </div>
                                        )} */}
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* ---------- Assign Modal ---------- */}
            <Modal isOpen={assignModal} toggle={closeAssignModal}>
                <ModalHeader toggle={closeAssignModal}>Assign Policy</ModalHeader>
                <ModalBody>
                    {assignError && <Alert color="danger">{assignError}</Alert>}
                    <div className="mb-3 p-2 bg-light rounded">
                        <small className="text-muted">Employee</small>
                        <div className="fw-semibold">{assignData.employee?.name}</div>
                        <small className="text-muted">
                            {assignData.employee?.employee_code}
                        </small>
                    </div>

                    {assignData.employee?.policy && (
                        <Alert color="warning">
                            Currently assigned:{' '}
                            <strong>{assignData.employee.policy.name}</strong>.
                            Assigning a new policy will deactivate it.
                        </Alert>
                    )}

                    <FormGroup>
                        <Label>Policy *</Label>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={assignData.policy_id}
                            onChange={e =>
                                setAssignData(p => ({
                                    ...p,
                                    policy_id: e.target.value
                                        ? Number(e.target.value)
                                        : '',
                                }))
                            }
                        >
                            <option value="">-- Select Policy --</option>
                            {policies
                                .filter(p => p.is_active)
                                .map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                        </Input>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={closeAssignModal} disabled={assigning}>
                        Cancel
                    </Button>
                    <Button color="success" onClick={handleAssign} disabled={assigning}>
                        {assigning ? (
                            <>
                                <Spinner size="sm" className="me-2" /> Assigning...
                            </>
                        ) : (
                            'Assign'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ---------- History Modal ---------- */}
            <Modal isOpen={historyModal} toggle={() => setHistoryModal(false)} size="lg">
                <ModalHeader toggle={() => setHistoryModal(false)}>
                    Assignment History — {historyEmployee?.name}
                </ModalHeader>
                <ModalBody>
                    {historyLoading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" className="me-2" />
                            <span>Loading history...</span>
                        </div>
                    ) : historyData.length === 0 ? (
                        <Alert color="info">No assignment history</Alert>
                    ) : (
                        <Table bordered size="sm">
                            <thead className="table-light">
                                <tr>
                                    <th>Policy</th>
                                    <th>From</th>
                                    <th>To</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyData.map((h, i) => (
                                    <tr key={i}>
                                        <td>{h.policy?.name || '—'}</td>
                                        <td>{formatDate(h.effective_from)}</td>
                                        <td>{h.effective_to ? formatDate(h.effective_to) : '—'}</td>
                                        <td>
                                            {h.is_active ? (
                                                <Badge color="success">Active</Badge>
                                            ) : (
                                                <Badge color="secondary">Inactive</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setHistoryModal(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ---------- Unassign Modal ---------- */}
            <Modal isOpen={unassignModal} toggle={closeUnassignModal}>
                <ModalHeader toggle={closeUnassignModal}>Unassign Policy</ModalHeader>
                <ModalBody>
                    <p>
                        Remove policy{' '}
                        <strong>"{unassignTarget?.policy?.name}"</strong> from{' '}
                        <strong>{unassignTarget?.name}</strong>?
                    </p>
                    <Alert color="warning" className="mb-0">
                        The employee will have no active leave policy. You can
                        re-assign later.
                    </Alert>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="light"
                        onClick={closeUnassignModal}
                        disabled={unassigning}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="danger"
                        onClick={handleUnassign}
                        disabled={unassigning}
                    >
                        {unassigning ? (
                            <>
                                <Spinner size="sm" className="me-2" /> Unassigning...
                            </>
                        ) : (
                            'Unassign'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default EmployeeLeavePolicy;